import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../../hooks/useCart';

interface ProductImage {
  url: string;
  altText: string;
}

interface Variant {
  id: string;
  title: string;
  price: string;
  availableForSale: boolean;
  quantityAvailable: number;
  selectedOptions: { name: string; value: string }[];
}

interface Option {
  id: string;
  name: string;
  values: string[];
}

interface ProductDetail {
  id: string;
  title: string;
  descriptionHtml: string;
  images: ProductImage[];
  variants: Variant[];
  options: Option[];
  priceRange: { min: string; max: string; currencyCode: string };
  tags: string[];
  availableForSale: boolean;
}

interface QuickViewModalProps {
  productId: string;
  productName: string;
  onClose: () => void;
}

export default function QuickViewModal({ productId, productName, onClose }: QuickViewModalProps) {
  const navigate = useNavigate();
  const { addItem } = useCart();

  const [product, setProduct] = useState<ProductDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeImage, setActiveImage] = useState(0);
  const [selectedOptions, setSelectedOptions] = useState<Record<string, string>>({});
  const [quantity, setQuantity] = useState(1);
  const [added, setAdded] = useState(false);

  const extractNumericId = (gid: string) => gid.split('/').pop() || '';

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  useEffect(() => {
    fetchProduct();
  }, [productId]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      setError('');
      const gid = `gid://shopify/Product/${productId}`;
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/shopify-product-detail`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ productId: gid }),
        }
      );
      if (!response.ok) throw new Error('Failed to fetch');
      const data = await response.json();
      const p = data.product;
      setProduct(p);
      if (p?.options?.length) {
        const defaults: Record<string, string> = {};
        p.options.forEach((opt: Option) => { if (opt.values.length) defaults[opt.name] = opt.values[0]; });
        setSelectedOptions(defaults);
      }
    } catch {
      setError('Could not load product details.');
    } finally {
      setLoading(false);
    }
  };

  const selectedVariant = useCallback((): Variant | null => {
    if (!product) return null;
    return product.variants.find((v) =>
      v.selectedOptions.every((o) => selectedOptions[o.name] === o.value)
    ) || product.variants[0] || null;
  }, [product, selectedOptions]);

  const variant = selectedVariant();
  const maxQty = variant?.quantityAvailable ?? 10;

  const handleAddToCart = () => {
    if (!variant || !product) return;
    addItem({
      id: variant.id,
      variantId: variant.id,
      name: product.title,
      price: parseFloat(variant.price),
      image: product.images[0]?.url || '',
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  const handleViewFull = () => {
    onClose();
    navigate(`/product/${productId}`);
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm"></div>

      {/* Modal */}
      <div
        className="relative z-10 w-full max-w-4xl max-h-[90vh] bg-[#0a0a0a] border border-[#00d4ff]/20 rounded-2xl overflow-hidden shadow-[0_0_80px_rgba(0,212,255,0.15)] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        style={{ animation: 'quickViewIn 0.3s ease' }}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-20 w-9 h-9 flex items-center justify-center rounded-full bg-black/60 border border-white/10 text-gray-400 hover:text-white hover:border-[#00d4ff]/40 transition-all duration-300 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-close-line text-lg"></i>
        </button>

        <div className="overflow-y-auto flex-1">
          {/* Loading */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-24 gap-4">
              <div className="w-12 h-12 border-4 border-[#00d4ff]/20 border-t-[#00d4ff] rounded-full animate-spin"></div>
              <p className="text-gray-500 text-xs tracking-widest uppercase">Loading...</p>
            </div>
          )}

          {/* Error */}
          {error && !loading && (
            <div className="flex flex-col items-center justify-center py-24 gap-4">
              <i className="ri-error-warning-line text-4xl text-red-400"></i>
              <p className="text-gray-400 text-sm">{error}</p>
            </div>
          )}

          {/* Content */}
          {!loading && !error && product && (
            <div className="flex flex-col md:flex-row">
              {/* Left — Images */}
              <div className="md:w-[45%] flex-shrink-0 p-5">
                <div className="relative w-full aspect-[4/5] bg-[#111] rounded-xl overflow-hidden">
                  <img
                    src={product.images[activeImage]?.url || ''}
                    alt={product.images[activeImage]?.altText || product.title}
                    className="w-full h-full object-cover object-top transition-opacity duration-400"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none"></div>
                  {product.images.length > 1 && (
                    <>
                      <button
                        onClick={() => setActiveImage((i) => (i - 1 + product.images.length) % product.images.length)}
                        className="absolute left-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-full bg-black/60 border border-[#00d4ff]/30 text-[#00d4ff] hover:bg-[#00d4ff]/20 transition-all duration-300 cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-arrow-left-s-line text-lg"></i>
                      </button>
                      <button
                        onClick={() => setActiveImage((i) => (i + 1) % product.images.length)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-full bg-black/60 border border-[#00d4ff]/30 text-[#00d4ff] hover:bg-[#00d4ff]/20 transition-all duration-300 cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-arrow-right-s-line text-lg"></i>
                      </button>
                    </>
                  )}
                </div>
                {/* Thumbnails */}
                {product.images.length > 1 && (
                  <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
                    {product.images.map((img, i) => (
                      <button
                        key={i}
                        onClick={() => setActiveImage(i)}
                        className={`flex-shrink-0 w-14 h-16 rounded-lg overflow-hidden border-2 transition-all duration-300 cursor-pointer ${
                          i === activeImage
                            ? 'border-[#00d4ff] shadow-[0_0_10px_rgba(0,212,255,0.4)]'
                            : 'border-transparent opacity-40 hover:opacity-70'
                        }`}
                      >
                        <img src={img.url} alt={img.altText || product.title} className="w-full h-full object-cover object-top" />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Right — Details */}
              <div className="md:w-[55%] p-6 flex flex-col gap-5 border-t md:border-t-0 md:border-l border-white/5">
                {/* Badge */}
                <div className="inline-flex items-center gap-2 w-fit px-3 py-1 border border-[#00d4ff]/30 rounded-full">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#00d4ff] animate-pulse"></span>
                  <span className="text-[#00d4ff] text-[10px] tracking-widest uppercase">Quick View</span>
                </div>

                {/* Title & Price */}
                <div>
                  <h2 className="text-white text-xl font-bold tracking-wide mb-2" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                    {product.title}
                  </h2>
                  <div className="flex items-baseline gap-2">
                    <span className="text-[#00d4ff] text-2xl font-semibold">
                      ${variant ? parseFloat(variant.price).toFixed(2) : product.priceRange.min}
                    </span>
                    <span className="text-gray-600 text-xs">{product.priceRange.currencyCode}</span>
                  </div>
                </div>

                {/* Availability */}
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${variant?.availableForSale ? 'bg-green-400' : 'bg-red-500'}`}></span>
                  <span className={`text-xs tracking-wider ${variant?.availableForSale ? 'text-green-400' : 'text-red-400'}`}>
                    {variant?.availableForSale ? 'In Stock' : 'Out of Stock'}
                  </span>
                </div>

                {/* Variants */}
                {product.options.length > 0 && (
                  <div className="space-y-4">
                    {product.options.map((option) => (
                      <div key={option.id}>
                        <p className="text-gray-400 text-xs tracking-widest uppercase mb-2">
                          {option.name}:{' '}
                          <span className="text-white font-semibold">{selectedOptions[option.name]}</span>
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {option.values.map((value) => {
                            const isSelected = selectedOptions[option.name] === value;
                            return (
                              <button
                                key={value}
                                onClick={() => setSelectedOptions((prev) => ({ ...prev, [option.name]: value }))}
                                className={`px-4 py-1.5 rounded-full text-xs font-semibold tracking-wide transition-all duration-300 cursor-pointer whitespace-nowrap ${
                                  isSelected
                                    ? 'bg-[#00d4ff] text-black shadow-[0_0_14px_rgba(0,212,255,0.5)]'
                                    : 'bg-[#1a1a1a] text-white border border-gray-700 hover:border-[#00d4ff]/60 hover:text-[#00d4ff]'
                                }`}
                              >
                                {value}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Quantity */}
                <div>
                  <p className="text-gray-400 text-xs tracking-widest uppercase mb-2">Quantity</p>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                      className="w-8 h-8 flex items-center justify-center rounded-full border border-white/10 text-gray-400 hover:border-[#00d4ff]/40 hover:text-[#00d4ff] transition-all duration-300 cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-subtract-line text-sm"></i>
                    </button>
                    <span className="text-white text-sm font-semibold w-6 text-center">{quantity}</span>
                    <button
                      onClick={() => setQuantity((q) => Math.min(maxQty || 10, q + 1))}
                      className="w-8 h-8 flex items-center justify-center rounded-full border border-white/10 text-gray-400 hover:border-[#00d4ff]/40 hover:text-[#00d4ff] transition-all duration-300 cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-add-line text-sm"></i>
                    </button>
                  </div>
                </div>

                {/* Description snippet */}
                {product.descriptionHtml && (
                  <div
                    className="text-gray-500 text-xs leading-relaxed line-clamp-3 border-t border-white/5 pt-4"
                    dangerouslySetInnerHTML={{ __html: product.descriptionHtml }}
                  />
                )}

                {/* Actions */}
                <div className="flex flex-col sm:flex-row gap-3 mt-auto pt-2">
                  <button
                    onClick={handleAddToCart}
                    disabled={!variant?.availableForSale}
                    className={`flex-1 py-3 rounded-full text-sm font-semibold tracking-widest uppercase transition-all duration-300 cursor-pointer whitespace-nowrap disabled:bg-gray-800 disabled:text-gray-500 disabled:cursor-not-allowed ${
                      added
                        ? 'bg-green-400 text-black'
                        : 'bg-[#00d4ff] text-black hover:bg-white'
                    }`}
                    style={!added && variant?.availableForSale ? { boxShadow: '0 0 20px rgba(0,212,255,0.4)' } : {}}
                  >
                    {added ? '✓ Added to Cart' : variant?.availableForSale ? 'Add to Cart' : 'Sold Out'}
                  </button>
                  <button
                    onClick={handleViewFull}
                    className="flex-1 py-3 rounded-full text-sm font-semibold tracking-widest uppercase border border-[#00d4ff]/30 text-[#00d4ff] hover:bg-[#00d4ff]/10 transition-all duration-300 cursor-pointer whitespace-nowrap"
                  >
                    View Full Details
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes quickViewIn {
          from { opacity: 0; transform: scale(0.95) translateY(10px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </div>
  );
}
